import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Icon } from '@iconify/react'
import { ClubCrest } from '../components/ClubCrest'
import { buildBossComebackSummary, buildMatchSimulation } from './simulation'
import { LeagueBlockTicker } from './LeagueBlockTicker'
import { WorldCupGroupTicker } from './WorldCupGroupTicker'
import { useMatchTeamColors } from './teamColors'
import type { MatchEvent, MatchOptions, MatchReactionHandler, MatchSummary } from './types'
import './MatchTicker.css'

interface MatchTickerProps {
  options: MatchOptions
  onComplete: (summary: MatchSummary) => void
  onMatchReaction?: MatchReactionHandler
}

const FIXED_PLAYBACK_RATE = 2.25
const REFERENCE_DURATION_MS = { normal: 14_500, derby: 17_000, boss: 19_000 } as const

function eventCopy(event: MatchEvent) {
  if (event.type === 'goal') return `Gol de ${event.actor}${event.assist ? `, asistencia de ${event.assist}` : ''}.`
  if (event.type === 'save') return `Parada de ${event.actor} al disparo de ${event.shooter}.`
  return `Falta de ${event.actor} sobre ${event.victim}${event.card ? '. Tarjeta amarilla.' : '.'}`
}

function isPlayerAction(event: MatchEvent, playerName: string): boolean {
  if (event.type === 'goal') return event.actor === playerName || event.assist === playerName
  if (event.type === 'save') return event.actor === playerName || event.shooter === playerName
  return event.actor === playerName || event.victim === playerName
}

const eventIcon = { goal: 'mdi:soccer', save: 'mdi:hand-back-right', foul: 'mdi:whistle' } as const

function SingleMatchTicker({ options, onComplete, onMatchReaction }: MatchTickerProps) {
  const fingerprint = `${options.matchId}-${options.expectedResult}-${options.assistBonusPercentagePoints ?? 0}`
  const simulation = useMemo(() => buildMatchSimulation({ ...options, durationMs: options.durationMs ?? REFERENCE_DURATION_MS[options.intensity] / FIXED_PLAYBACK_RATE }), [fingerprint])
  const comebackSummary = useMemo(() => buildBossComebackSummary(simulation, options), [options.homeSquadNames, options.player.name, simulation])
  const [minute, setMinute] = useState(0)
  const [finished, setFinished] = useState(false)
  const [paused, setPaused] = useState(false)
  const [visibleEvents, setVisibleEvents] = useState<MatchEvent[]>([])
  const [reactionWon, setReactionWon] = useState<boolean | null>(null)
  const feedRef = useRef<HTMLDivElement>(null)
  const timerRef = useRef<number | null>(null)
  const elapsedRef = useRef(0)
  const pausedRef = useRef(false)
  const reactionOpenedRef = useRef(false)
  const reactionWonRef = useRef(false)
  const teamColors = useMatchTeamColors(options.homeTeam, simulation.opponent)
  const commentaryColors = {
    '--home-action-color': teamColors.home,
    '--away-action-color': teamColors.away,
  } as CSSProperties

  useEffect(() => {
    setMinute(0); setFinished(false); setPaused(false); setVisibleEvents([]); setReactionWon(null)
    elapsedRef.current = 0
    pausedRef.current = false
    reactionOpenedRef.current = false
    reactionWonRef.current = false
    let previousTick = performance.now()
    timerRef.current = window.setInterval(() => {
      const now = performance.now()
      if (!pausedRef.current) elapsedRef.current += now - previousTick
      previousTick = now
      if (pausedRef.current) return
      const nextMinute = Math.min(90, Math.floor(elapsedRef.current / simulation.durationMs * 90))
      setMinute(nextMinute)
      const activeEvents = reactionWonRef.current ? comebackSummary.events : simulation.events
      setVisibleEvents(activeEvents.filter((event) => event.minute <= nextMinute))
      if (nextMinute >= (simulation.lateReactionMinute ?? Number.POSITIVE_INFINITY) && !reactionOpenedRef.current && onMatchReaction && (options.intensity === 'boss' || options.intensity === 'derby') && simulation.outcome === 'loss' && simulation.awayGoals === simulation.homeGoals + 1) {
        reactionOpenedRef.current = true
        pausedRef.current = true
        setPaused(true)
        void onMatchReaction({
          matchId: simulation.matchId, opponent: simulation.opponent, homeGoals: simulation.homeGoals, awayGoals: simulation.awayGoals,
          playerGoals: simulation.playerGoals, playerAssists: simulation.playerAssists, outcome: simulation.outcome, events: simulation.events,
        }, simulation.lateReactionMinute ?? nextMinute).then((won) => {
          setReactionWon(won)
          if (won) {
            reactionWonRef.current = true
            pausedRef.current = false
            setPaused(false)
            return
          }
          elapsedRef.current = simulation.durationMs
          setMinute(90)
          setVisibleEvents([...simulation.events])
          setFinished(true)
          if (timerRef.current !== null) window.clearInterval(timerRef.current)
          timerRef.current = null
        })
        return
      }
      if (nextMinute >= 90) {
        if (timerRef.current !== null) window.clearInterval(timerRef.current)
        timerRef.current = null
        setFinished(true)
      }
    }, 100)
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [simulation])

  useEffect(() => {
    const feed = feedRef.current
    if (!feed) return
    if (typeof feed.scrollTo === 'function') feed.scrollTo({ top: 0, behavior: 'smooth' })
    else feed.scrollTop = 0
  }, [visibleEvents])

  const homeGoals = visibleEvents.filter((event) => event.type === 'goal' && event.team === 'home').length
  const awayGoals = visibleEvents.filter((event) => event.type === 'goal' && event.team === 'away').length
  const baseSummary: MatchSummary = {
    matchId: simulation.matchId, opponent: simulation.opponent,
    homeGoals: simulation.homeGoals, awayGoals: simulation.awayGoals,
    playerGoals: simulation.playerGoals, playerAssists: simulation.playerAssists,
    playerMinutes: simulation.playerMinutes,
    outcome: simulation.outcome === 'win' ? 'win' : 'loss',
    events: simulation.events,
  }
  const summary: MatchSummary = reactionWon
    ? comebackSummary
    : baseSummary

  const togglePause = () => {
    setPaused((current) => {
      pausedRef.current = !current
      return !current
    })
  }

  const simulateResult = () => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current)
    timerRef.current = null
    pausedRef.current = false
    elapsedRef.current = simulation.durationMs
    setPaused(false)
    setMinute(90)
    setVisibleEvents([...simulation.events])
    setFinished(true)
  }

  return (
    <section className="match-ticker" aria-label={`${options.title}: ${options.homeTeam} contra ${simulation.opponent}`}>
      <header className="match-ticker__header">
        <p className="eyebrow">{options.title}</p>
      </header>
      <div className="match-ticker__scoreboard" aria-live="polite">
        <div className="match-ticker__team"><ClubCrest name={options.homeTeam} size="large" /><strong title={options.homeTeam}>{options.homeTeam}</strong></div>
        <div className="match-ticker__score-unit">
          <div className="match-ticker__score"><span>{finished ? summary.homeGoals : homeGoals}</span><i>—</i><span>{finished ? summary.awayGoals : awayGoals}</span></div>
          <span className={`match-ticker__minute${finished ? ' is-final' : ''}`}>{finished ? 'Final' : paused ? `${minute}' · Pausa` : `${minute}'`}</span>
        </div>
        <div className="match-ticker__team match-ticker__team--away"><ClubCrest name={simulation.opponent} size="large" /><strong title={simulation.opponent}>{simulation.opponent}</strong></div>
      </div>
      <div className="match-ticker__time" role="progressbar" aria-label="Minutos jugados" aria-valuemin={0} aria-valuemax={90} aria-valuenow={minute} aria-valuetext={`${minute} minutos${paused ? ', partido pausado' : ''}`}><span style={{ transform: `scaleX(${minute / 90})` }} /></div>

      <section className="commentary-box" aria-labelledby="commentary-title" style={commentaryColors}>
        <header><h3 id="commentary-title">Acciones del partido</h3><span>{finished ? 'Partido terminado' : 'En directo'}</span></header>
        <div ref={feedRef} className="commentary-box__feed" aria-live="polite" aria-relevant="additions">
          {[...visibleEvents].sort((left, right) => right.minute - left.minute).map((event) => {
            const teamName = event.team === 'home' ? options.homeTeam : simulation.opponent
            const playerAction = isPlayerAction(event, options.player.name)
            return <article className={`is-${event.type} is-team-${event.team}${playerAction ? ' is-player-action' : ''}`} aria-label={`${playerAction ? 'Tu jugador. ' : ''}${teamName}: ${eventCopy(event)}`} key={`${event.minute}-${event.type}-${event.actor}`}><time>{event.minute}&apos;</time><span className="commentary-box__event-crest" title={teamName}><ClubCrest name={teamName} size="small" /></span><Icon icon={eventIcon[event.type]} /><p>{playerAction && <strong className="commentary-box__player-label">Tu jugador</strong>}{eventCopy(event)}</p></article>
          })}
          <article><time>1&apos;</time><span className="commentary-box__neutral-team" aria-hidden="true" /><Icon icon="mdi:whistle-outline" /><p>Comienza el partido.</p></article>
          {!finished && <p className="commentary-box__waiting"><span /> {paused ? 'Partido pausado.' : 'El partido sigue en juego…'}</p>}
        </div>
      </section>

      <footer className="match-ticker__footer match-ticker__footer--controls-only">
        {finished ? (
          <button className="button button--primary" type="button" onClick={() => onComplete(summary)}>Continuar</button>
        ) : (
          <div className="match-ticker__controls">
            <button className="button match-ticker__pause" type="button" aria-pressed={paused} onClick={togglePause}><Icon icon={paused ? 'mdi:play' : 'mdi:pause'} /> {paused ? 'Reanudar' : 'Pausar'}</button>
            <button className="button button--primary" type="button" onClick={simulateResult}><Icon icon="mdi:skip-forward" /> Simular hasta el resultado</button>
          </div>
        )}
      </footer>
    </section>
  )
}

export function MatchTicker(props: MatchTickerProps) {
  if (props.options.format === 'world-cup-group') return <WorldCupGroupTicker {...props} />
  return props.options.format === 'league-block'
    ? <LeagueBlockTicker {...props} />
    : <SingleMatchTicker {...props} />
}
